// The Demographics panel: the first card in the dashboard's 2x2 grid, one tab
// each for gender, age, pre-existing conditions and DSM-5 diagnosis.
//
// Same shell as the three panels beside it — Paper, p:3, radius 3, 380 tall —
// so the row reads as one set rather than one card and three others.
//
// Chart type is chosen per tab rather than for visual consistency, because the
// shape of each question is different:
//   gender     — two or three slices, so a donut reads at a glance and matches
//                the two donuts already on this dashboard.
//   age        — ordered bands. A donut throws the order away, which is the
//                only thing an age chart is for, so it is a column chart.
//   conditions
//   diagnosis  — DSM-5 alone has ~950 possible values. A donut with forty
//                slices is a colour swatch, not a chart, so these are ranked
//                horizontal bars with the tail collapsed into "Other".
//
// The counting rules live in the ⓘ rather than as caption text under the
// chart: at this card size every line of prose is a line the chart does not
// get, and the two that matter only apply to two of the four tabs.
import React, { useState } from 'react';
import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DashboardInfoTooltip, { InfoRow, InfoText } from './DashboardInfoTooltip.js';
import { NONE_RECORDED, NOT_SPECIFIED, OTHER } from './clientDemographics.js';

const BAR_COLOR = '#29C0BB';

// Sized to the 380px panel: title, subtitle and tab strip take the rest.
const CHART_HEIGHT = 210;

// A DSM-5 name can run to forty characters, which at this width would push the
// bars off the card. The axis truncates and the hover tooltip carries the full
// label, so nothing is actually lost.
const AXIS_LABEL_MAX = 18;
const truncate = (value) =>
  String(value).length > AXIS_LABEL_MAX ? `${String(value).slice(0, AXIS_LABEL_MAX - 1)}…` : value;

const TABS = [
  { key: 'gender', label: 'Gender', kind: 'donut' },
  { key: 'age', label: 'Age', kind: 'column' },
  { key: 'conditions', label: 'Conditions', kind: 'ranked' },
  { key: 'diagnosis', label: 'DSM-5', kind: 'ranked' },
];

const DemographicsInfo = () => (
  <DashboardInfoTooltip
    label="How Demographics are counted"
    heading="How this is counted"
    trackId="dashboard.demographics.info"
  >
    <InfoText sx={{ mb: 1 }}>
      Every client in the current caseload, counted once. Age is worked out from date
      of birth, so it is current rather than whatever was recorded at sign-up.
    </InfoText>

    <Stack spacing={0.5} sx={{ mb: 1 }}>
      <InfoRow term={NOT_SPECIFIED}>nothing recorded for that client</InfoRow>
      <InfoRow term={NONE_RECORDED}>recorded as having none</InfoRow>
      <InfoRow term={`${OTHER} (n)`}>the n smallest values, grouped to fit the chart</InfoRow>
    </Stack>

    <InfoText sx={{ mb: 1 }}>
      Unlike the panels above, this one is not affected by the date range — a caseload
      is who the practice sees now, not who it saw this week.
    </InfoText>

    <InfoText strong>
      Conditions and DSM-5 are multi-select, so one client can appear in several bars
      and those percentages add up to more than 100%.
    </InfoText>
  </DashboardInfoTooltip>
);

const Empty = ({ children }) => (
  <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

export default function ClientDemographicsCard({ demographics }) {
  const [tab, setTab] = useState(0);

  const data = demographics || {};
  const total = data.total || 0;
  const active = TABS[tab];
  const rows = data[active.key] || [];

  // Percentages are of clients, so the tooltip says both numbers: a bar reading
  // "12" means twelve clients, not twelve tags. The name comes from the row
  // rather than the series — recharts would otherwise label every slice "count",
  // and this is the only place a truncated DSM-5 name is shown in full.
  const tooltipFormatter = (value, _name, entry) => {
    const pct = entry?.payload?.percent;
    const label = `${value} ${value === 1 ? 'client' : 'clients'}`;
    return [
      typeof pct === 'number' ? `${label} (${pct}%)` : label,
      entry?.payload?.name ?? '',
    ];
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, height: { xs: 'auto', md: 380 }, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Demographics
        </Typography>
        <DemographicsInfo />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {total === 0
          ? 'No clients in this caseload'
          : `Across ${total} ${total === 1 ? 'client' : 'clients'}`}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mt: 0.5,
          minHeight: 36,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 36, py: 0, px: 1.5, fontSize: 13, fontWeight: 600 },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} data-track-id={`dashboard.demographics.${t.key}`} />
        ))}
      </Tabs>

      <Box sx={{ mt: 1, flex: 1, minHeight: 0 }}>
        {rows.length === 0 ? (
          <Empty>Nothing recorded yet</Empty>
        ) : active.kind === 'donut' ? (
          <Grid container spacing={1} sx={{ height: '100%' }}>
            <Grid size={6}>
              <Box sx={{ height: CHART_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={rows} dataKey="count" innerRadius={45} outerRadius={72} isAnimationActive={false}>
                      {rows.map((row) => (
                        <Cell key={row.name} fill={row.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={tooltipFormatter} separator=": " />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            <Grid size={6}>
              <Box sx={{ overflowY: 'auto', maxHeight: CHART_HEIGHT, pr: 0.5 }}>
                <Stack spacing={0.75}>
                  {rows.map((row) => (
                    <Stack key={row.name} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: row.color, flexShrink: 0 }} />
                        <Typography variant="caption" noWrap>{row.name}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.5, flexShrink: 0 }}>
                        {row.count} ({row.percent}%)
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        ) : active.kind === 'column' ? (
          <Box sx={{ height: CHART_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={tooltipFormatter} separator=": " cursor={{ fill: 'rgba(41,192,187,0.08)' }} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ height: CHART_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={118}
                  interval={0}
                  tick={{ fontSize: 11 }}
                  tickFormatter={truncate}
                />
                <Tooltip formatter={tooltipFormatter} separator=": " cursor={{ fill: 'rgba(41,192,187,0.08)' }} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
